import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Api } from '../application/panelApi';
import { ExportCsvModal } from '../components/ExportCsvModal';
import { ICON } from '../components/Icons';
import { LaneIcon } from '../components/LaneIcon';
import { Meter, RefreshIcon } from '../components/Meter';
import { Modal } from '../components/Modal';
import { confirmDialog } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import type { CsvExport } from '../features/csvExport';
import {
  clLiveStats,
  clPillInfo,
  clusterOccupancy,
  clusterEngines,
  clusterOsLabel,
  clustersCsv,
  engineLabel,
  formatAgentBuild,
  isLiveStatus,
  matchesClusterFilter,
  nodesNeedingAgentUpdate,
  nodeUpdateLabel,
  nodeUpdateState,
  setPendingLogCluster,
  setPendingProfileCluster,
  takePendingServerFocus,
  shortDateTime,
  type Cluster,
  type ClusterEngine,
  type ControlBuild,
} from '../features/clusters';
import { useClusterLive } from '../hooks/useClusterLive';
import { errText, gb, nf, plural, sizeStr } from '../lib/format';
import { useApp } from '../store/AppContext';



type ClusterBody = {
  name: string;
  region?: string;
  os: 'linux' | 'windows';
  sshHost: string;
  sshPort: number;
  sshUser: string;
  profileLimit: number;
  enabled: boolean;
  searchEngines: ClusterEngine[];
  autoDeploy?: boolean;
  sshPassword?: string;
};

type ClusterFormProps = {
  cluster: Cluster | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

function ClusterFormModal({ cluster: c, onClose, onSaved }: ClusterFormProps) {
  const { audit } = useApp();
  const engines = clusterEngines(c);
  const [os, setOs] = useState<'linux' | 'windows'>(c?.os === 'windows' ? 'windows' : 'linux');
  const [name, setName] = useState(c?.name ?? '');
  const [region, setRegion] = useState(c?.region ?? '');
  const [host, setHost] = useState(c?.sshHost ?? '');
  const [port, setPort] = useState(String(c ? c.sshPort : 22));
  const [user, setUser] = useState(
    c ? c.sshUser || (c.os === 'windows' ? 'Administrator' : 'root') : 'root',
  );
  const [pass, setPass] = useState('');
  const [limit, setLimit] = useState(String(c ? c.profileLimit || 120 : 120));
  const [enabled, setEnabled] = useState(!c || c.enabled !== false);
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState(false);

  const win = os === 'windows';

  const changeOs = (next: 'linux' | 'windows') => {
    setOs(next);
    if (!user.trim() || user === 'root' || user === 'Administrator') {
      setUser(next === 'windows' ? 'Administrator' : 'root');
    }
  };

  const readBody = (): ClusterBody => {
    const body: ClusterBody = {
      name: name.trim(),
      region: region.trim() || undefined,
      os,
      sshHost: host.trim(),
      sshPort: Math.min(65535, Math.max(1, +port || 22)),
      sshUser: user.trim() || (win ? 'Administrator' : 'root'),
      profileLimit: Math.max(1, +limit || 120),
      enabled,
      searchEngines: engines.length ? engines : ['bing', 'google'],
    };
    if (!c) body.autoDeploy = auto;
    if (pass) body.sshPassword = pass;
    return body;
  };

  const save = async () => {
    const body = readBody();
    if (c) {
      if (!body.name || !body.sshHost) {
        toast('Укажите имя ноды и IP сервера, затем сохраните снова.', 'err');
        return;
      }
      setBusy(true);
      try {
        await Api.clusters.update(c.id, body);
        onClose();
        await onSaved();
        audit('Изменил кластер', c.name);
        toast('Изменения ноды сохранены.', 'ok');
      } catch (err) {
        toast(errText(err), 'err');
        setBusy(false);
      }
      return;
    }
    if (!body.name) {
      toast('Укажите имя ноды, например eu-1.', 'err');
      return;
    }
    if (!body.sshHost) {
      toast('Укажите IP сервера, к которому панель подключится по SSH.', 'err');
      return;
    }
    if (!body.sshPassword) {
      toast(
        `Укажите SSH-пароль пользователя ${body.os === 'windows' ? 'Administrator' : 'root'}, иначе панель не сможет подключиться к серверу.`,
        'err',
      );
      return;
    }
    setBusy(true);
    try {
      const data = await Api.clusters.create(body);
      onClose();
      toast(
        data.deploying
          ? `Нода ${body.name} добавлена. Агент устанавливается в фоне, прогресс виден в столбце «Статус».`
          : `Нода ${body.name} добавлена.`,
        'ok',
      );
      if (data.hasSshPassword === false) {
        toast(
          'SSH-пароль не сохранился. Откройте ноду через «Изменить» и введите пароль ещё раз.',
          'err',
        );
      }
      audit('Добавил кластер', `${body.name} · ${body.sshHost}`);
      void onSaved().catch(() => undefined);
    } catch (err) {
      toast(errText(err), 'err');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={c ? `Изменить кластер · ${c.name}` : 'Добавить кластер'}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" data-close onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            id="cSave"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy && !c ? 'Добавление...' : c ? 'Сохранить' : 'Добавить'}
          </button>
        </>
      }
    >
      <div className="field-row">
        <label htmlFor="cOs">ОС сервера</label>
        <select
          id="cOs"
          value={os}
          onChange={(e) => changeOs(e.target.value === 'windows' ? 'windows' : 'linux')}
        >
          <option value="linux">Linux (Ubuntu / Debian · Docker)</option>
          <option value="windows">Windows Server</option>
        </select>
      </div>
      <div className="grid2">
        <div className="field-row">
          <label htmlFor="cName">Имя кластера</label>
          <input
            type="text"
            id="cName"
            value={name}
            placeholder="Например: eu-1"
            spellCheck={false}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field-row">
          <label htmlFor="cRegion">Регион</label>
          <input
            type="text"
            id="cRegion"
            value={region}
            placeholder="Например: EU · Frankfurt"
            spellCheck={false}
            onChange={(e) => setRegion(e.target.value)}
          />
        </div>
      </div>
      <div className="grid3">
        <div className="field-row">
          <label htmlFor="cHost">IP сервера</label>
          <input
            type="text"
            id="cHost"
            value={host}
            placeholder="Например: 10.20.0.11"
            spellCheck={false}
            onChange={(e) => setHost(e.target.value)}
          />
        </div>
        <div className="field-row">
          <label htmlFor="cPort">SSH-порт</label>
          <input
            type="number"
            id="cPort"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
        </div>
        <div className="field-row">
          <label htmlFor="cUser">SSH-пользователь</label>
          <input
            type="text"
            id="cUser"
            value={user}
            spellCheck={false}
            onChange={(e) => setUser(e.target.value)}
          />
        </div>
      </div>
      <div className="field-row">
        <label htmlFor="cPass">SSH-пароль</label>
        <input
          type="password"
          id="cPass"
          value={pass}
          placeholder={
            c?.hasSshPassword
              ? 'Оставьте пустым, чтобы не менять пароль'
              : win
                ? 'Пароль пользователя Administrator'
                : 'Пароль пользователя root'
          }
          autoComplete="new-password"
          onChange={(e) => setPass(e.target.value)}
        />
      </div>
      <div className="grid2">
        <div className="field-row">
          <label>Ядер CPU</label>
          <input
            type="text"
            id="cCores"
            readOnly
            placeholder="Заполнится автоматически после подключения"
            value={c?.cores ? String(c.cores) : ''}
            style={{ color: 'var(--text-2)' }}
          />
        </div>
        <div className="field-row">
          <label>ОЗУ, ГБ</label>
          <input
            type="text"
            id="cRam"
            readOnly
            placeholder="Заполнится автоматически после подключения"
            value={c?.ramTotalGb != null ? String(Math.round(c.ramTotalGb * 10) / 10) : ''}
            style={{ color: 'var(--text-2)' }}
          />
        </div>
      </div>
      <div className="field-row">
        <label htmlFor="cLimit">Лимит профилей</label>
        <input
          type="number"
          id="cLimit"
          min={1}
          max={5000}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
        />
      </div>
      <div className="set-row">
        <div className="txt">
          <b>Нода включена</b>
          <small>
            Выключенная нода не получает новые задачи. Включить её снова можно в любой момент.
          </small>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            id="cOn"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <i />
        </label>
      </div>
      {!c && (
        <div className="set-row">
          <div className="txt">
            <b>Автодеплой агента</b>
            <small>
              Панель сама установит агент на сервер сразу после добавления. Если выключить,
              запустите установку позже кнопкой «Обновить агент».
            </small>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              id="cAuto"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
            />
            <i />
          </label>
        </div>
      )}
    </Modal>
  );
}



type RecentLog = { t?: string; timestamp?: string; lvl?: string; text?: string; msg?: string };

const LVL_NAME: Record<string, string> = { ok: 'успех', wa: 'внимание', er: 'ошибка' };

function logTime(l: RecentLog): string {
  const d = new Date(String(l.timestamp || l.t || ''));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU');
}

function Fact({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="sd-fact">
      <span>{k}</span>
      <b>{v}</b>
    </div>
  );
}

type DetailProps = {
  c: Cluster;
  onPing: () => Promise<void>;
  onDeploy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onProfiles: () => void;
  onLogs: () => void;
};

function ServerDetail({ c, onPing, onDeploy, onEdit, onDelete, onProfiles, onLogs }: DetailProps) {
  const { controlBuild } = useApp();
  const live = clLiveStats(c);
  const occ = clusterOccupancy(c);
  const pill = clPillInfo(c);
  const off = c.status === 'offline' || c.status === 'error' || c.status === 'pending';
  const deploying = c.status === 'deploying';
  const hide = off || (!deploying && !c.lastPingAt);
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState<RecentLog[] | null>(null);
  const [tick, setTick] = useState(0);
  const n = (v: number | undefined) => (hide || v == null ? '—' : nf.format(v));

  useEffect(() => {
    let alive = true;
    setLogs(null);
    void (async () => {
      try {
        const data = (await Api.logs.report(`limit=12&clusterId=${encodeURIComponent(c.id)}`)) as {
          logs?: RecentLog[];
        };
        if (alive) setLogs(data.logs ?? []);
      } catch {
        if (alive) setLogs([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [c.id, tick]);

  const refresh = async () => {
    setBusy(true);
    try {
      await onPing();
      setTick((v) => v + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={`sd os-${c.os === 'windows' ? 'windows' : 'linux'}`}>
      <header className="sd-head">
        <img
          className="sd-os"
          src={`/brand/${c.os === 'windows' ? 'windows' : 'linux'}.png`}
          alt=""
          width={44}
          height={44}
        />
        <div className="sd-title">
          <h2>
            {c.name}
            <span className={`pill ${pill.cls}`}>
              <span className="led" />
              {pill.title}
            </span>
            <BuildPill control={controlBuild} revision={c.agentRevision} />
          </h2>
          <small>
            {clusterOsLabel(c)} · {c.sshHost}:{c.sshPort}
            {c.region ? ` · ${c.region}` : ''}
            {c.hostname ? ` · ${c.hostname}` : ''}
          </small>
        </div>
        <button
          type="button"
          className="btn btn-primary sd-refresh"
          disabled={busy || deploying}
          onClick={() => void refresh()}
        >
          {RefreshIcon}
          {busy ? 'Обновляем…' : 'Обновить данные'}
        </button>
      </header>

      {deploying ? (
        <Meter
          value={c.deployProgress ?? 0}
          label={c.deployStageLabel || `${c.deployProgress ?? 0}%`}
        />
      ) : null}
      {c.deployError ? <div className="sd-err">{c.deployError}</div> : null}

      <section className="sd-sec">
        <h3>Нагрузка</h3>
        <div className="sd-meters">
          <div className="sv-metric">
            <span className="sv-k">Занято воркеров</span>
            {hide || !occ.cap ? (
              DASH
            ) : (
              <Meter
                value={occ.running}
                max={occ.cap}
                label={`${nf.format(occ.running)} из ${nf.format(occ.cap)}`}
              />
            )}
            {!hide && occ.queued ? (
              <span className="sv-sub">
                Ждут свободного воркера: {nf.format(occ.queued)}{' '}
                {plural(occ.queued, 'задание', 'задания', 'заданий')}
              </span>
            ) : null}
          </div>
          <div className="sv-metric">
            <span className="sv-k">CPU · {c.cores || '—'} ядер</span>
            {hide ? DASH : <Meter value={live.cpuPct} label={`${live.cpuPct}%`} />}
          </div>
          <div className="sv-metric">
            <span className="sv-k">ОЗУ</span>
            {hide ? (
              DASH
            ) : (
              <Meter
                value={live.ramUsedGb}
                max={c.ramTotalGb}
                label={`${gb(live.ramUsedGb)} / ${c.ramTotalGb != null ? gb(c.ramTotalGb) : '—'}`}
              />
            )}
          </div>
          <div className="sv-metric">
            <span className="sv-k">Диск</span>
            {hide || live.profilesDiskMb == null || live.profilesDiskTotalMb == null ? (
              DASH
            ) : (
              <Meter
                value={live.profilesDiskMb / 1024}
                max={live.profilesDiskTotalMb / 1024}
                label={`${sizeStr(live.profilesDiskMb)} / ${sizeStr(live.profilesDiskTotalMb)}`}
              />
            )}
          </div>
          <div className="sv-metric">
            <span className="sv-k">Прокси в работе</span>
            {hide || !live.proxiesTotal ? (
              DASH
            ) : (
              <Meter
                value={live.proxiesUsed}
                max={live.proxiesTotal}
                label={`${nf.format(live.proxiesUsed)} из ${nf.format(live.proxiesTotal)}`}
              />
            )}
          </div>
        </div>
      </section>

      <section className="sd-sec">
        <h3>Профили</h3>
        <div className="sd-facts">
          <Fact k="Браузер открыт" v={n(live.profilesActive)} />
          <Fact k="Ждут запуска" v={n(live.profilesIdle)} />
          <Fact k="Всего на сервере" v={n(live.profilesTotal)} />
          <Fact k="Лимит профилей" v={c.profileLimit ? nf.format(c.profileLimit) : '—'} />
          <Fact k="На подготовке" v={n(live.profilesWarming)} />
          <Fact k="Подготовлены" v={n(live.profilesReady)} />
          <Fact k="Готовы к кликам" v={n(live.profilesReadyForTarget)} />
          <Fact k="Готовить сегодня" v={n(live.profilesDueToday)} />
        </div>
      </section>

      <section className="sd-sec">
        <h3>Подключение</h3>
        <div className="sd-facts">
          <Fact
            k="SSH"
            v={
              <code>
                {c.sshUser || '—'}@{c.sshHost}:{c.sshPort}
              </code>
            }
          />
          <Fact k="Пароль SSH" v={c.hasSshPassword ? 'сохранён' : 'не задан'} />
          <Fact
            k="Агент"
            v={
              <>
                <span className="mono">{formatAgentBuild(c.agentVersion, c.agentRevision)}</span>
                {' '}
                <BuildPill control={controlBuild} revision={c.agentRevision} />
              </>
            }
          />
          <Fact k="Контроль" v={<span className="mono">{controlBuild?.shortRevision || '—'}</span>} />
          <Fact
            k="Последняя проверка"
            v={
              c.lastPingAt
                ? `${shortDateTime(c.lastPingAt)}${c.lastPingMs != null ? ` · ${c.lastPingMs} мс` : ''}`
                : '—'
            }
          />
          <Fact k="Поисковики" v={clusterEngines(c).map(engineLabel).join(' · ')} />
          <Fact k="Работа" v={c.enabled ? 'включён' : 'выключен'} />
        </div>
      </section>

      <section className="sd-sec">
        <div className="sd-sec-head">
          <h3>Последние события</h3>
          <button type="button" className="lp-link" onClick={onLogs}>
            Все логи сервера
          </button>
        </div>
        {logs == null ? (
          <div className="empty">Загружаем…</div>
        ) : logs.length ? (
          <ul className="sd-logs">
            {logs.map((l, i) => (
              <li key={`${l.timestamp ?? l.t ?? ''}-${i}`} className={l.lvl || undefined}>
                <time>{logTime(l)}</time>
                {l.lvl && LVL_NAME[l.lvl] ? (
                  <span className={`lv ${l.lvl}`}>{LVL_NAME[l.lvl]}</span>
                ) : null}
                <span className="sd-log-text">
                  {l.text ?? String(l.msg ?? '').replace(/<[^>]*>/g, '')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty">Событий нет</div>
        )}
      </section>

      <footer className="sd-actions">
        <button type="button" className="sv-act" onClick={onProfiles}>
          {ICON.eye}
          Профили сервера
        </button>
        <button type="button" className="sv-act" onClick={onDeploy}>
          {DEPLOY_ICON}
          {deploying ? 'Переустановить агент заново' : 'Обновить агент'}
        </button>
        <button type="button" className="sv-act" onClick={onEdit}>
          {ICON.edit}
          Изменить
        </button>
        <button type="button" className="sv-act danger" onClick={onDelete}>
          {ICON.del}
          Удалить
        </button>
      </footer>
    </article>
  );
}



const DASH = (
  <span className="mono" style={{ color: 'var(--text-3)' }}>
    —
  </span>
);

const WINDOWS_LOGO = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="8.4" height="8.4" rx=".6" />
    <rect x="12.6" y="3" width="8.4" height="8.4" rx=".6" />
    <rect x="3" y="12.6" width="8.4" height="8.4" rx=".6" />
    <rect x="12.6" y="12.6" width="8.4" height="8.4" rx=".6" />
  </svg>
);

const TUX_LOGO = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <ellipse cx="12" cy="12.6" rx="6.8" ry="9" fill="#111" />
    <ellipse cx="12" cy="15" rx="4.5" ry="5.8" fill="#fff" />
    <circle cx="10.2" cy="7.4" r="1.4" fill="#fff" />
    <circle cx="13.8" cy="7.4" r="1.4" fill="#fff" />
    <circle cx="10.4" cy="7.6" r=".6" fill="#111" />
    <circle cx="13.6" cy="7.6" r=".6" fill="#111" />
    <path d="M10.2 9.5h3.6L12 11.6z" fill="#FCC624" />
    <ellipse cx="8.4" cy="21.3" rx="2.9" ry="1.4" fill="#FCC624" />
    <ellipse cx="15.6" cy="21.3" rx="2.9" ry="1.4" fill="#FCC624" />
  </svg>
);

const DEPLOY_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5" />
    <path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
  </svg>
);

const STATUS_TABS: Array<[string, string]> = [
  ['all', 'Все'],
  ['online', 'Онлайн'],
  ['busy', 'Нагружен'],
  ['off', 'Оффлайн'],
  ['stale', 'Не обновлены'],
];

function BuildPill({
  control,
  revision,
}: {
  control?: ControlBuild | null;
  revision?: string;
}) {
  const state = nodeUpdateState(control, revision);
  return (
    <span className={`pill build ${state}`} title={control?.revision ? `контроль ${control.shortRevision}` : undefined}>
      {nodeUpdateLabel(state)}
    </span>
  );
}

type RowProps = {
  c: Cluster;
  onView: () => void;
  onPing: () => void;
  onDeploy: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

function ClusterRow({ c, onView, onPing, onDeploy, onEdit, onDelete }: RowProps) {
  const { controlBuild } = useApp();
  const [open, setOpen] = useState(false);
  const live = clLiveStats(c);
  const occ = clusterOccupancy(c);
  const off = c.status === 'offline' || c.status === 'error' || c.status === 'pending';
  const deploying = c.status === 'deploying';
  const noMetrics = !off && !deploying && !c.lastPingAt;
  const hide = off || noMetrics;
  const ping = c.lastPingAt
    ? `${shortDateTime(c.lastPingAt)}${c.lastPingMs != null ? ` · ${c.lastPingMs} мс` : ''}`
    : '—';
  const errColor = deploying ? 'var(--warn)' : 'var(--err)';
  const pill = clPillInfo(c);

  const diskKnown = !hide && live.profilesDiskMb != null && live.profilesDiskTotalMb != null;

  const osCls = `os-${c.os === 'windows' ? 'windows' : 'linux'}`;
  const head = (
    <header className="sv-card-head">
      <div className="sv-card-title">
        <span className="sv-name">
          <b>{c.name}</b>
          <span className="sv-os">
            {c.os === 'windows' ? WINDOWS_LOGO : TUX_LOGO}
            {clusterOsLabel(c)}
          </span>
        </span>
        <small>
          {c.sshHost}:{c.sshPort}
          {c.region ? ` · ${c.region}` : ''}
        </small>
      </div>
      <span className="sv-pills">
        <span className={`pill ${pill.cls}`}>
          <span className="led" />
          {pill.title}
        </span>
        <BuildPill control={controlBuild} revision={c.agentRevision} />
      </span>
    </header>
  );
  const meta = (
    <span className="sv-meta">
      {deploying && c.deployStartedAt ? `с ${shortDateTime(c.deployStartedAt)}` : `пинг ${ping}`}
      {` · агент ${formatAgentBuild(c.agentVersion, c.agentRevision)}`}
    </span>
  );

  return (
    <article
      className={`sv-cell${open ? ' open' : ''}`}
      data-id={c.id}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={() => setOpen((v) => !v)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
    >
      <div className="sv-flip">
        <div className={`sv-card sv-front ${osCls}`} inert={open}>
          {head}
          {deploying ? (
            <Meter value={c.deployProgress ?? 0} label={`${c.deployProgress ?? 0}%`} />
          ) : null}
          {noMetrics ? (
            <div className="sv-err" style={{ color: 'var(--warn)' }}>
              Метрик пока нет
            </div>
          ) : null}
          {c.deployError ? (
            <div className="sv-err" style={{ color: errColor }}>
              {c.deployError}
            </div>
          ) : null}
          <div className="sv-metrics">
            <div className="sv-metric">
              <span className="sv-k">Браузеры и задача</span>
              {hide || !occ.cap ? (
                DASH
              ) : (
                <Meter
                  value={occ.running}
                  max={occ.cap}
                  label={`запущено ${nf.format(occ.running)} из ${nf.format(occ.cap)} (доступно ${nf.format(occ.available)})`}
                />
              )}
              {!hide ? (
                <span className="sv-sub">
                  Задача:{' '}
                  {occ.farm > 0 && occ.target > 0
                    ? `Фарм (${occ.farm}) · Таргеты (${occ.target})`
                    : occ.target > 0 || live.workMode === 'targeting'
                      ? `Таргеты (${occ.target || occ.running})`
                      : occ.farm > 0 || live.workMode === 'warming' || (live.profilesWarming ?? 0) > 0
                        ? `Фарм (${occ.farm || occ.running})`
                        : 'Простой'}
                  {occ.queued ? ` · в очереди: ${nf.format(occ.queued)}` : ''}
                </span>
              ) : null}
            </div>
            <div className="sv-metric">
              <span className="sv-k">CPU · {c.cores || '—'} ядер</span>
              {hide ? DASH : <Meter value={live.cpuPct} label={`${live.cpuPct}%`} />}
            </div>
            <div className="sv-metric">
              <span className="sv-k">ОЗУ</span>
              {hide ? (
                DASH
              ) : (
                <Meter
                  value={live.ramUsedGb}
                  max={c.ramTotalGb}
                  label={`${gb(live.ramUsedGb)} / ${c.ramTotalGb != null ? gb(c.ramTotalGb) : '—'}`}
                />
              )}
            </div>
            <div className="sv-metric">
              <span className="sv-k">Диск</span>
              {diskKnown ? (
                <Meter
                  value={(live.profilesDiskMb ?? 0) / 1024}
                  max={(live.profilesDiskTotalMb ?? 0) / 1024}
                  label={`${sizeStr(live.profilesDiskMb ?? 0)} / ${sizeStr(live.profilesDiskTotalMb ?? 0)}`}
                />
              ) : (
                DASH
              )}
            </div>
            <div className="sv-metric">
              <span className="sv-k">Занятость</span>
              {noMetrics || off ? (
                DASH
              ) : (
                <Meter value={live.loadPct || 0} label={`${live.loadPct || 0}%`} />
              )}
            </div>
          </div>
          <div className="sv-prof-wrap">
            <span className="sv-k">Профили на сервере</span>
            <div className="sv-profiles">
              <div title="Профили, у которых сейчас открыт браузер">
                <b>{noMetrics ? '—' : nf.format(live.profilesActive)}</b>
                <span>браузер открыт</span>
              </div>
              <div title="Профили, которые сейчас не запущены">
                <b>{noMetrics ? '—' : nf.format(live.profilesIdle)}</b>
                <span>ждут запуска</span>
              </div>
              <div>
                <b>{noMetrics ? '—' : nf.format(live.profilesTotal)}</b>
                <span>всего</span>
              </div>
            </div>
          </div>
          <footer className="sv-card-foot">{meta}</footer>
        </div>

        <div className={`sv-card sv-back ${osCls}`} inert={!open}>
          {head}
          <div
            className="sv-actions"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button type="button" className="sv-act" data-act="view" onClick={onView}>
              {ICON.eye}
              Подробности
            </button>
            <button
              type="button"
              className="sv-act"
              data-act="ping"
              disabled={deploying}
              onClick={onPing}
            >
              {RefreshIcon}
              Проверить связь
            </button>
            <button type="button" className="sv-act" data-act="deploy" onClick={onDeploy}>
              {DEPLOY_ICON}
              {deploying ? 'Переустановить агент заново' : 'Обновить агент'}
            </button>
            <button type="button" className="sv-act" data-act="edit" onClick={onEdit}>
              {ICON.edit}
              Изменить
            </button>
            <button type="button" className="sv-act danger" data-act="del" onClick={onDelete}>
              {ICON.del}
              Удалить
            </button>
          </div>
          <footer className="sv-card-foot">{meta}</footer>
        </div>
      </div>
    </article>
  );
}



export function ClustersPage() {
  const {
    clusters: rawClusters,
    controlBuild,
    controlMode,
    setClusters,
    setControlBuild,
    refreshClusters,
    goto,
    audit,
  } = useApp();
  const clusters = rawClusters as Cluster[];
  const control = controlMode === 'control';

  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setViewState] = useState<'grid' | 'single'>(() =>
    localStorage.getItem('sv-view') === 'single' ? 'single' : 'grid',
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const setView = (next: 'grid' | 'single') => {
    setViewState(next);
    localStorage.setItem('sv-view', next);
  };
  const openServer = (id: string) => {
    setSelectedId(id);
    setView('single');
  };
  useEffect(() => {
    const focus = takePendingServerFocus();
    if (focus) openServer(focus);
  }, []);
  const [form, setForm] = useState<{ cluster: Cluster | null } | null>(null);
  const [exp, setExp] = useState<CsvExport | null>(null);

  useClusterLive();


  useEffect(() => {
    if (!control) return undefined;
    let alive = true;
    void (async () => {
      try {
        const data = await Api.clusters.list();
        if (!alive) return;
        const list = (data.clusters || []) as Cluster[];
        setClusters(list);
        if (data.control) setControlBuild(data.control);
        const needPing = list.some((c) => c.enabled && isLiveStatus(c) && !c.lastPingAt);
        if (needPing) {
          await Api.clusters.pingAll();
          if (alive) await refreshClusters();
        }
      } catch {

      }
    })();
    return () => {
      alive = false;
    };
  }, [control, setClusters, setControlBuild, refreshClusters]);

  const rows = useMemo(
    () => (control ? clusters.filter((c) => matchesClusterFilter(c, filter, statusFilter, controlBuild)) : []),
    [control, clusters, filter, statusFilter, controlBuild],
  );


  const kpi = useMemo(() => {
    const liveNodes = clusters.filter(isLiveStatus);
    const act = clusters.reduce((a, c) => a + clusterOccupancy(c).running, 0);
    const totalProf = liveNodes.reduce((a, c) => a + clLiveStats(c).profilesTotal, 0);
    const ram = clusters.reduce((a, c) => a + clLiveStats(c).ramUsedGb, 0);
    const ramAll = liveNodes.reduce((a, c) => a + (c.ramTotalGb || 0), 0);
    const cpu = liveNodes.length
      ? Math.round(liveNodes.reduce((a, c) => a + clLiveStats(c).cpuPct, 0) / liveNodes.length)
      : 0;
    const stale = clusters.filter((c) => nodeUpdateState(controlBuild, c.agentRevision) === 'stale').length;
    return { live: liveNodes.length, act, totalProf, ram, ramAll, cpu, stale };
  }, [clusters, controlBuild]);

  const reload = async () => {
    await refreshClusters();
  };

  const pingOne = async (c: Cluster) => {
    if (c.status === 'deploying') {
      toast(
        `${c.name}: проверка недоступна, пока устанавливается агент (${c.deployStageLabel || 'в процессе'}). Дождитесь окончания установки.`,
        'wa',
      );
      return;
    }
    try {
      await Api.clusters.ping(c.id);
      await reload();
      toast(`Нода ${c.name} проверена, данные обновлены.`, 'ok');
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const deployOne = (c: Cluster) => {
    const run = async () => {
      try {
        await Api.clusters.deploy(c.id);
        await reload();
        audit('Обновил агент', c.name);
        toast(`Обновление агента на ${c.name} запущено. Прогресс виден в столбце «Статус».`, 'ok');
      } catch (err) {
        toast(errText(err), 'err');
      }
    };
    if (c.status === 'deploying') {
      confirmDialog(
        'Начать установку агента заново?',
        <>
          На <b>{c.name}</b> уже идёт установка агента ({c.deployStageLabel || 'в процессе'}).
          Текущая установка прервётся и начнётся с начала.
        </>,
        () => void run(),
        { yes: 'Перезапустить' },
      );
      return;
    }
    void run();
  };

  const deleteOne = (c: Cluster) => {
    confirmDialog(
      'Удалить ноду?',
      <>
        Нода <b>{c.name}</b> ({c.sshHost || 'IP не указан'}) будет удалена из панели безвозвратно.
        <br />
        Профили и агент на самом сервере останутся, но панель перестанет ими управлять и забудет
        токен, DNS и статусы ноды.
        <br />
        <b>Это действие нельзя отменить.</b>
      </>,
      () => {
        void (async () => {
          try {
            await Api.clusters.remove(c.id);
            await reload();
            audit('Удалил кластер', c.name);
            toast(`Нода ${c.name} удалена из панели.`, 'err');
          } catch (err) {
            toast(errText(err), 'err');
          }
        })();
      },
      { word: `удалить ${c.name}`, yes: 'Удалить навсегда' },
    );
  };

  const pingAll = async () => {
    try {
      const r = await Api.clusters.pingAll();
      await reload();
      const results = (r.results || []) as Array<{ online?: boolean; deploying?: boolean }>;
      const online = results.filter((x) => x.online).length;
      const deploying = results.filter((x) => x.deploying).length;
      const msg = deploying
        ? `Проверено нод: ${clusters.length}. На связи: ${online}, устанавливают агент: ${deploying}.`
        : `Проверка завершена: на связи ${online} из ${clusters.length} нод.`;
      toast(msg, online === clusters.length && !deploying ? 'ok' : 'wa');
      audit('Проверил все кластеры', `${online}/${clusters.length}`);
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const updateAll = () => {
    const outdated = nodesNeedingAgentUpdate(clusters, controlBuild);
    if (!outdated.length) {
      toast('Обновлять нечего: все включённые ноды уже на ревизии контроля или обновляются.');
      return;
    }
    confirmDialog(
      'Обновить агент на нодах?',
      <>
        Агент будет переустановлен на <b>{outdated.length}</b>{' '}
        {plural(outdated.length, 'ноде', 'нодах', 'нодах')}
        {controlBuild?.shortRevision ? (
          <>
            {' '}
            до ревизии контроля <code>{controlBuild.shortRevision}</code>
          </>
        ) : null}
        . Обновление идёт в фоне, прогресс виден в столбце «Статус».
      </>,
      () => {
        void (async () => {
          let ok = 0;
          for (const c of outdated) {
            try {
              await Api.clusters.deploy(c.id);
              ok += 1;
            } catch {

            }
          }
          await reload().catch(() => undefined);
          audit('Обновил агент', `${ok} нод`);
          toast(
            `Обновление агента запущено на ${ok} из ${outdated.length} нод. Если запустились не все, проверьте SSH-доступ к остальным.`,
            ok ? 'ok' : 'err',
          );
        })();
      },
      { yes: 'Обновить' },
    );
  };

  const selected = rows.find((c) => c.id === selectedId) ?? rows[0] ?? null;

  return (
    <section id="page-clusters" className="sv">
      <div className="sv-head">
        <div className="sv-title">
          <h1 className="h1-lane">
            <LaneIcon kind="servers" />
            Сервера
          </h1>
          <p className="sv-facts">
            <span className={`sv-online${control && kpi.live ? '' : ' off'}`} id="clOn">
              <i />
              {control ? kpi.live : 0} из {control ? clusters.length : 0} онлайн
            </span>
            <span id="clProf">
              <b>{control ? nf.format(kpi.act) : 0}</b> браузеров открыто из{' '}
              {control ? nf.format(kpi.totalProf) : 0}
            </span>
            <span id="clCpu">
              CPU в среднем{' '}
              <b className={control && kpi.cpu >= 85 ? 'err' : undefined}>
                {control ? kpi.cpu : 0}%
              </b>
            </span>
            <span id="clRam">
              ОЗУ{' '}
              {control && kpi.ramAll ? (
                <>
                  <b>{gb(kpi.ram)}</b> из {gb(kpi.ramAll)}
                </>
              ) : (
                '—'
              )}
            </span>
            <span id="clBuild">
              контроль{' '}
              <b className="mono">{control && controlBuild?.shortRevision ? controlBuild.shortRevision : '—'}</b>
              {control && kpi.stale ? (
                <>
                  {' · '}
                  <b className="err">{kpi.stale} не обновлены</b>
                </>
              ) : null}
            </span>
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn" id="clPingAll" onClick={() => void pingAll()}>
            Проверить все
          </button>
          <button type="button" className="btn" id="clUpdAll" onClick={updateAll}>
            Обновить агент
          </button>
          <button
            type="button"
            className="btn btn-primary sv-add"
            id="clAddBtn"
            aria-label="Добавить сервер"
            title="Добавить сервер"
            onClick={() => setForm({ cluster: null })}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </div>

      <div className="sv-toolbar">
        <input
          type="text"
          id="clFilter"
          placeholder="Поиск по имени, IP или региону"
          spellCheck={false}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="lp-seg" id="clStatusFilter" role="radiogroup" aria-label="Статус">
          {STATUS_TABS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={statusFilter === id}
              className={statusFilter === id ? 'on' : undefined}
              onClick={() => setStatusFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="lp-seg" id="clView" role="radiogroup" aria-label="Вид">
          <button
            type="button"
            role="radio"
            aria-checked={view === 'grid'}
            className={view === 'grid' ? 'on' : undefined}
            onClick={() => setView('grid')}
          >
            Сетка
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={view === 'single'}
            className={view === 'single' ? 'on' : undefined}
            onClick={() => setView('single')}
          >
            По одному
          </button>
        </div>
        <span className="sv-count" id="clCount">
          {control ? `${rows.length} из ${clusters.length}` : '0'}
        </span>
        <button
          type="button"
          className="btn"
          data-export="clusters"
          onClick={() => setExp(clustersCsv(clusters, controlBuild))}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v11m-4-4 4 4 4-4M4 16v4h16v-4" /></svg>
          Экспорт CSV
        </button>
      </div>

      {!control ? (
        <div className="empty">Доступно только на управляющем сервере</div>
      ) : view === 'grid' ? (
        <div className="sv-grid" id="clBody">
          {rows.map((c) => (
            <ClusterRow
              key={c.id}
              c={c}
              onView={() => openServer(c.id)}
              onPing={() => void pingOne(c)}
              onDeploy={() => deployOne(c)}
              onEdit={() => setForm({ cluster: c })}
              onDelete={() => deleteOne(c)}
            />
          ))}
        </div>
      ) : (
        <div className="sv-single" id="clBody">
          <nav className="sv-list" aria-label="Сервера">
            {rows.map((c) => {
              const p = clPillInfo(c);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`sv-list-item${selected?.id === c.id ? ' on' : ''}`}
                  aria-current={selected?.id === c.id ? 'true' : undefined}
                  onClick={() => setSelectedId(c.id)}
                >
                  <img
                    src={`/brand/${c.os === 'windows' ? 'windows' : 'linux'}.png`}
                    alt=""
                    width={24}
                    height={24}
                  />
                  <span className="sv-list-text">
                    <b>{c.name}</b>
                    <small>{c.region || c.sshHost}</small>
                  </span>
                  <span className={`sv-dot ${p.cls}`} title={p.title} />
                  {nodeUpdateState(controlBuild, c.agentRevision) === 'stale' ? (
                    <span className="sv-stale" title="агент не обновлён">
                      !
                    </span>
                  ) : null}
                </button>
              );
            })}
          </nav>
          {selected ? (
            <ServerDetail
              key={selected.id}
              c={selected}
              onPing={() => pingOne(selected)}
              onDeploy={() => deployOne(selected)}
              onEdit={() => setForm({ cluster: selected })}
              onDelete={() => deleteOne(selected)}
              onProfiles={() => {
                setPendingProfileCluster(selected.id);
                goto('profiles');
              }}
              onLogs={() => {
                setPendingLogCluster(selected.id);
                goto('logs');
              }}
            />
          ) : (
            <div className="empty">Нод не найдено</div>
          )}
        </div>
      )}

      {form && (
        <ClusterFormModal
          key={form.cluster?.id ?? 'new'}
          cluster={form.cluster}
          onClose={() => setForm(null)}
          onSaved={reload}
        />
      )}
      {exp && <ExportCsvModal name={exp.name} csv={exp.csv} onClose={() => setExp(null)} />}
    </section>
  );
}
